import React, { useState, useContext } from 'react';

import { Lightbulb, AlertTriangle, Send, CheckCircle, Info } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import SuggestionsWidget from '../components/dashboard/SuggestionsWidget';

const Suggestions = () => {
  const { createSuggestion } = useData();
  const { currentUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState('improvement'); // 'improvement' או 'issue'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    department: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('נא למלא את כל השדות הנדרשים');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await createSuggestion({
        ...formData,
        type: activeTab,
        userId: currentUser.id,
        userName: currentUser.name || currentUser.email,
        status: 'pending',
        createdAt: new Date()
      });

      setShowSuccess(true);
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        department: ''
      });

      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    } catch (err) {
      setError('אירעה שגיאה בשליחת ההודעה. אנא נסה שוב.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="p-6 border-b">
          <h1 className="text-2xl font-bold mb-2">הצעות ייעול ודיווח על תקלות</h1>
          <p className="text-gray-600">שתף את הרעיונות שלך לשיפור או דווח על בעיות שנתקלת בהן</p>
        </div>

        {/* Tabs */}
        <div className="p-6 pb-0">
          <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('improvement')}
              className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
                activeTab === 'improvement' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Lightbulb size={20} />
                הצעת ייעול ושיפור
              </div>
            </button>
            <button
              onClick={() => setActiveTab('issue')}
              className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
                activeTab === 'issue' 
                  ? 'bg-white text-red-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <AlertTriangle size={20} />
                דיווח על תקלה
              </div>
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6">
          {showSuccess && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">ההודעה נשלחה בהצלחה!</div>
                <div className="text-sm">תודה על תרומתך לשיפור המערכת. ההנהלה תבחן את הודעתך בהקדם.</div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* כותרת */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                כותרת *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder={activeTab === 'improvement' 
                  ? "תאר בקצרה את ההצעה שלך" 
                  : "תאר בקצרה את התקלה"
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* תיאור מפורט */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                תיאור מפורט *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={activeTab === 'improvement' 
                  ? "פרט את ההצעה שלך: מה הבעיה הנוכחית? מה הפתרון המוצע? אילו יתרונות צפויים?" 
                  : "תאר את התקלה: מתי זה קרה? מה ניסית לעשות? מה היו התוצאות?"
                }
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* עדיפות */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {activeTab === 'improvement' ? 'חשיבות' : 'דחיפות'}
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">נמוכה</option>
                  <option value="medium">בינונית</option>
                  <option value="high">גבוהה</option>
                  {activeTab === 'issue' && <option value="critical">קריטית</option>}
                </select>
              </div>

              {/* מחלקה רלוונטית */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  מחלקה רלוונטית (אופציונלי)
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  placeholder="לדוגמה: מחלקת IT, משאבי אנוש..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* הסבר על התהליך */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900 mb-2">מה קורה אחרי השליחה?</h3>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>ההודעה שלך תועבר ישירות להנהלה</li>
                  <li>תקבל עדכון כאשר ההודעה תטופל</li>
                  <li>כל ההצעות והדיווחים נבדקים ומטופלים בהתאם לסדר עדיפות</li>
                </ul>
              </div>
            </div>

            {/* כפתור שליחה */}
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
                className={`px-6 py-3 ${
                  activeTab === 'improvement' 
                    ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300' 
                    : 'bg-red-600 hover:bg-red-700 disabled:bg-red-300'
                } text-white rounded-lg transition-colors flex items-center gap-2 font-medium disabled:cursor-not-allowed`}
              >
                <Send size={20} />
                {isSubmitting ? 'שולח...' : 'שלח הודעה'}
              </button>
            </div>
          </form>
        </div>
      </div>
          {/* הצגת הווידג'ט רק למנכ"ל */}
    {(currentUser?.role === 'ceo' || currentUser?.role === 'org_admin') && (
      <div className="mt-8">
        <SuggestionsWidget />
      </div>
    )}
    </div>
  );
};

export default Suggestions;